import React from 'react';
import { HelpCircle, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';

const FAQ = () => {
  const faqs = [
    {
      question: 'Do you offer climate-controlled units?',
      answer: 'We focus exclusively on drive-up, non-climate units. This allows us to offer lower prices and easier access than our competitors. These units are perfect for furniture, tools, cars, and general household goods.'
    },
    {
      question: 'Can I store my boat or RV here?',
      answer: 'Yes! We offer paved outdoor parking spaces for boats, trailers, and RVs. Visit our Boat & RV Storage page for more details.'
    },
    {
      question: 'When can I access my unit?',
      answer: 'Gate access is available from 6:00 AM to 10:00 PM, 7 days a week. You can get to your stuff even when the office is closed.'
    },
    {
      question: 'Do I need insurance?',
      answer: 'We offer affordable tenant protection plans during the checkout process to ensure your items are covered against unexpected events. You can also use your existing homeowners or renters insurance if it covers items in storage.'
    },
    {
      question: 'How do I rent a unit?',
      answer: 'You can rent online 24/7 through our website. It takes just 2 minutes! You can also call us during office hours at (800) 768-7640 for personalized assistance.'
    },
    {
      question: 'What forms of payment do you accept?',
      answer: 'We accept all major credit cards, debit cards, and ACH payments online. You can set up autopay for convenient monthly payments.'
    },
    {
      question: 'Is there a deposit required?',
      answer: 'No security deposit is required. You just pay your first month\'s rent and any applicable fees to get started.'
    },
    {
      question: 'Can I change unit sizes?',
      answer: 'Yes! If you need more or less space, just contact our office and we\'ll help you transfer to a different size unit based on availability.'
    },
    {
      question: 'What items are not allowed in storage?',
      answer: 'We do not allow hazardous materials, flammable liquids, perishable food, illegal items, firearms, or living in units. See our Tips & Rules page for complete details.'
    },
    {
      question: 'Is there a long-term contract?',
      answer: 'No! We offer month-to-month rentals with no long-term commitment. You can move out anytime with proper notice.'
    },
    {
      question: 'Do you sell moving supplies?',
      answer: 'Yes, we have boxes, locks, packing tape, and other moving supplies available for purchase. Call ahead to confirm availability.'
    },
    {
      question: 'How secure is the facility?',
      answer: 'Very secure! We have 24/7 video surveillance, electronic gate access with personalized codes, and a fully fenced perimeter. Only tenants can access the facility.'
    }
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Storage FAQs | V&D Storage Near Richland Chambers Lake"
        description="Answers to common questions about storage units, boat & RV parking, access hours, payments, and more at V&D Storage on FM 416, near Richland Chambers Lake."
        canonical="/faq"
        structuredData={faqSchema}
      />
      {/* Header */}
      <section className="bg-green-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <HelpCircle className="h-16 w-16 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="faq-title">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-green-100">
            Get answers to common questions about our storage facility
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6" data-testid={`faq-${index}`}>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span>{faq.question}</span>
                </h3>
                <p className="text-gray-700 ml-9">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Still Have Questions?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Our friendly team is here to help you find the perfect storage solution.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:8007687640"
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors shadow-md"
              data-testid="call-button"
            >
              Call (800) 768-7640
            </a>
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-green-600 border-2 border-green-600 rounded-lg hover:bg-green-50 transition-colors"
              data-testid="contact-button"
            >
              Contact Us Online
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;